import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Select, Space } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { useDebouncedCallback } from "use-debounce";
import { ALL_CATEGORIES_API, REWARDS_FILTER_DATA_API } from "../../../API/paths";
import { DATAGRID_DEFAULT_PAGE_SIZE } from "../../../Constants/ApplicationConstants";
import { BASE_URL } from "../../../Constants/EnvironmentVariableProvider";
import { RoutesLookup } from "../../../Routes/RoutesLookup";
import TableComponent from "../../components/Table/TableComponent";
import AdminAppLayout from "../../Layouts/AdminLayout";
import { colors } from "../../utils/colors";
import {
  generateSearchParamsGetter,
  getNewSearchParamStringWithArgs,
  getSearchParams,
} from "../../utils/helpers";
import { getColumns } from "./columns";

const { Option } = Select;
const RewardInventory = () => {
  let [spObj, setSearchParams] = useSearchParams({
    page: "1",
    pageSize: DATAGRID_DEFAULT_PAGE_SIZE.toString(),
  });
  spObj.get('page') === null && spObj.set('page', '1');
  spObj.get('pageSize') === null && spObj.set('pageSize', DATAGRID_DEFAULT_PAGE_SIZE.toString());

  // eslint-disable-next-line no-unused-vars
  let [id, page, pageSize, filterName, statusType, statusItem, category_keyids] = getSearchParams(spObj);

  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState(
    category_keyids ? category_keyids.split(',').map(id => parseInt(id)) : []
  );

  const getNewSearchParamString = generateSearchParamsGetter(spObj);
  const debounceFetcher = useDebouncedCallback((search) => {
    setSearchParams(
      getNewSearchParamStringWithArgs({ page: 1, pageSize, search, statusType, statusItem, category_keyids })
    );
  }, 600);
  const statusApi =
    BASE_URL + REWARDS_FILTER_DATA_API + getNewSearchParamString();
  const {
    data: rewards,
    isValidating: loading,
    mutate,
  } = useSWR({
    url: statusApi,
  });

  // Fetch categories for filter dropdown
  const { data: categoriesData } = useSWR({
    url: BASE_URL + ALL_CATEGORIES_API,
  });

  useEffect(() => {
    if (mutate) mutate();
  }, [filterName, pageSize, page, mutate, statusType, category_keyids]);

  // Sync selectedCategories with URL parameter
  useEffect(() => {
    if (category_keyids) {
      const ids = category_keyids.split(',').map(id => parseInt(id));
      setSelectedCategories(ids);
    } else {
      setSelectedCategories([]);
    }
  }, [category_keyids]);

  const onPaginationChange = async (page, pageSize) => {
    setSearchParams(
      getNewSearchParamStringWithArgs({
        page,
        pageSize,
        statusType,
        statusItem,
        search: filterName,
        category_keyids,
      })
    );
  };

  const handleCategoryChange = (values) => {
    setSelectedCategories(values);
    const categoryIdsString = values && values.length > 0 ? values.join(',') : undefined;
    setSearchParams(
      getNewSearchParamStringWithArgs({
        page: 1,
        pageSize,
        statusType,
        statusItem,
        search: filterName,
        category_keyids: categoryIdsString,
      })
    );
  };

  const [searchState, setSearchState] = useState(filterName ? filterName : '')
  useEffect(() => {
    setSearchState(filterName)
  }, [filterName])

  const handleSearch = (e) => {
    debounceFetcher(e.target.value);
    setSearchState(e.target.value)
  };

  const [activeTab, setActiveTab] = useState(
    statusType ? parseInt(statusType) : 0
  );
  const [dataLoading, setDataLoading] = useState(false);

  const columns = getColumns({ navigate, page, pageSize, statusType });

  useEffect(() => {
    setTimeout(() => {
      setDataLoading(false);
    }, 1000);
  }, [dataLoading]);

  useEffect(() => {
    setActiveTab(parseInt(statusType ?? 0))
  }, [statusType])

  let statusTabs = [
    {
      btnText: "All",
      total: rewards?.allCount,
      onClick: () => {
        setActiveTab(0);
        setDataLoading(true);
        setSearchParams(
          getNewSearchParamStringWithArgs({
            page: 1,
            pageSize,
            statusType: 0,
            statusItem,
            search: filterName,
            category_keyids,
          })
        );
      },
    },
    {
      btnText: "Active",
      total: rewards?.activeCount,
      onClick: () => {
        setActiveTab(1);
        setSearchParams(
          getNewSearchParamStringWithArgs({
            page: 1,
            pageSize,
            statusType: 1,
            statusItem,
            search: filterName,
            category_keyids,
          })
        );
        setDataLoading(true);
      },
    },
    {
      btnText: "Inactive",
      total: rewards?.inActiveCount,
      onClick: () => {
        setActiveTab(2);
        setDataLoading(true);
        setSearchParams(
          getNewSearchParamStringWithArgs({
            page: 1,
            pageSize,
            statusType: 2,
            statusItem,
            search: filterName,
            category_keyids,
          })
        );
      },
    },
    {
      btnText: "Draft",
      total: rewards?.draftCount,
      onClick: () => {
        setActiveTab(3);
        setDataLoading(true);
        setSearchParams(
          getNewSearchParamStringWithArgs({
            page: 1,
            pageSize,
            statusType: 3,
            statusItem,
            search: filterName,
            category_keyids,
          })
        );
      },
    },
    {
      btnText: "Published",
      total: rewards?.publishedCount,
      onClick: () => {
        setActiveTab(4);
        setDataLoading(true);
        setSearchParams(
          getNewSearchParamStringWithArgs({
            page: 1,
            pageSize,
            statusType: 4,
            statusItem,
            search: filterName,
            category_keyids,
          })
        );
      },
    },
  ]

  return (
    <AdminAppLayout>
      <div
        style={{
          paddingLeft: 60,
          paddingRight: 60,
          marginTop: 56,
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <Space direction="horizontal" size={40}>
            {statusTabs.map((item, i) => (
              <Button
                style={{
                  color: activeTab === i && colors.grey900,
                  backgroundColor: activeTab === i && colors.grayNeutral100,
                }}
                key={item.btnText}
                onClick={item.onClick}
                type="text"
              >
                {item.btnText} ({item.total})
              </Button>
            ))}
          </Space>

          <div
            style={{
              display: "flex",
              gap: 12,
              width: 680,
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Select
              mode="multiple"
              size="large"
              placeholder="Filter by Category"
              value={selectedCategories}
              onChange={handleCategoryChange}
              allowClear
              style={{ width: 240 }}
              maxTagCount="responsive"
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {categoriesData?.result?.map((category) => (
                <Option key={category.ARLC_KeyID} value={category.ARLC_KeyID}>
                  {category.ARLC_Title}
                </Option>
              ))}
            </Select>
            <Input
              size="large"
              placeholder="Search Rewards..."
              defaultValue={filterName}
              value={searchState}
              onChange={handleSearch}
              allowClear
              prefix={
                <SearchOutlined
                  style={{
                    color: "rgba(0,0,0,.45)",
                  }}
                />
              }
            />
                {/* <Search
                  size="large"
                  placeholder="Search Reward..."
                  allowClear
                  value={searchState}
                  style={{ width: "120%"}}
                  onChange={(e) => setSearchState(e.target.value)}
                  onSearch={(value) => {
                    handleSearch({ target: { value } });
                  }}
                /> */}
            <Button
              onClick={() => {
                navigate(
                  `${RoutesLookup.REWARD_INVENTORY}/create?page=${page ?? 1
                  }&pageSize=${pageSize}`
                );
              }}
              style={{
                marginLeft: 20,
                marginTop: 5,
              }}
              type="primary"
            >
              + Add new reward
            </Button>
          </div>
        </div>
        <div style={{ marginTop: 28 }}>
          <TableComponent
            loading={loading}
            data={rewards?.result}
            totalRecords={rewards?.totalRecords}
            columns={columns}
            border
            rowkey="ACAG_KeyID"
            pagination={{
              totalPage: rewards?.totalPages * pageSize,
              pageSize: pageSize,
            }}
            onPaginationChange={onPaginationChange}
            isServerPagination
          />
        </div>
      </div>
    </AdminAppLayout>
  );
};

export default RewardInventory;
